/**
 * NBC Data Helpers
 *
 * Utilities for working with NBC API data and merging with Player API.
 *
 * @module utils/nbc/nbcHelpers
 */

import type { NBCSingleProductResponse, NBCProductListItem, RoomType } from '@/types/schemas/nbc';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

/**
 * Create a Map for fast NBC data lookup
 *
 * Converts array of NBC products into a Map with productId as key
 * for O(1) lookup performance when merging with Player data.
 *
 * @param nbcProducts - Array of NBC products
 * @returns Map with productId as key
 *
 * @example
 * const nbcResult = await getProductList(['A', 'B', 'C']);
 * const nbcMap = createNbcLookupMap(nbcResult.products);
 *
 * // Fast O(1) lookup
 * const nbcData = nbcMap.get(productId);
 *
 * @example Merging with Player products
 * const playerProducts = await getPlayerProductList();
 * const nbcResult = await getProductList(playerProducts.map(p => p.id));
 * const nbcMap = createNbcLookupMap(nbcResult.products);
 *
 * const enriched = playerProducts.map(p => ({
 *   ...p,
 *   nbc: nbcMap.get(p.id)
 * }));
 */
export function createNbcLookupMap<T extends { productId: string }>(
  nbcProducts: T[]
): Map<string, T> {
  return new Map(nbcProducts.map((product) => [product.productId, product]));
}

/**
 * Get first teaser image URL or fallback
 *
 * Safely extracts the first teaser image from NBC product data
 * with optional fallback value.
 *
 * @param product - NBC product list item
 * @param fallback - Fallback URL if no images available
 * @returns Image URL or fallback
 *
 * @example Basic usage
 * const imageUrl = getFirstTeaserImage(product, '/placeholder.jpg');
 *
 * @example In Vue template
 * <img :src="getFirstTeaserImage(nbcProduct, '/default-hotel.jpg')" alt="Hotel" />
 *
 * @example With optional chaining
 * const url = getFirstTeaserImage(nbcProduct) ?? playerProduct.mainImage;
 */
export function getFirstTeaserImage(
  product: NBCProductListItem,
  fallback?: string
): string | undefined {
  return product.teaserImages[0] || fallback;
}

/**
 * Get room type images for specific room code
 *
 * Extracts images for a specific room type from NBC product data.
 * Returns empty array if room type not found.
 *
 * @param product - NBC single product
 * @param roomCode - Room type code
 * @returns Array of room images or empty array
 *
 * @example
 * const product = await getProduct('3123123');
 * const roomImages = getRoomImages(product, 'A1');
 *
 * roomImages.forEach(image => {
 *   console.log(`Thumbnail: ${image.thumbnail}`);
 *   console.log(`Large: ${image.large}`);
 *   console.log(`Caption: ${image.caption || 'No caption'}`);
 * });
 *
 * @example In Vue component
 * const roomA1Images = getRoomImages(nbcProduct, 'A1');
 * if (roomA1Images.length > 0) {
 *   // Render image gallery
 * }
 */
export function getRoomImages(
  product: NBCSingleProductResponse,
  roomCode: string
): RoomType['roomTypeImages'] {
  const roomType = product.roomTypes.find((rt) => rt.roomTypeCode === roomCode);
  return roomType?.roomTypeImages || [];
}

/**
 * Safely extract and sanitize HTML content with fallback
 *
 * NBC HTML fields are nullable and may contain empty tags.
 *
 * @param htmlContent - HTML string (nullable)
 * @param fallback - Fallback text if content is empty
 * @returns Sanitized HTML content or fallback
 *
 * @example Basic usage
 * const highlights = extractHtmlOrFallback(
 *   product.highlights,
 *   '<p>No highlights available</p>'
 * );
 *
 * @example Vue template usage
 * <div v-html="extractHtmlOrFallback(product.description, '<p>No description</p>')"></div>
 *
 * @example Chained fallbacks
 * const content = extractHtmlOrFallback(product.highlights) ??
 *                 extractHtmlOrFallback(product.description) ??
 *                 playerProduct.description;
 */
export function extractHtmlOrFallback(
  htmlContent: string | null,
  fallback?: string
): string | undefined {
  if (!htmlContent) return fallback;

  const sanitized = sanitizeHtml(htmlContent);

  const textContent = sanitized.replace(/<[^>]*>/g, '').trim();

  return textContent.length > 0 ? sanitized : fallback;
}
